<!--
  Proxy redirection page

  ## Description

  Setting up a frontend redirection system based on URL query params,
  and agnostic to framework/applicative changes

  ## How it works
  - based on a URL query param ?layout_item=<string>
  - providing any other data within the URL query params ?foo=bar&baz=...
  - update/evolution: opportunistic, if a new set of redirection is required,
    a new frontend release will be necessary

-->
<script lang="ts">
import Console from '~/utils/lib/core/console'
import { includes, not, pipe_, propOr, toString, values } from '~/utils/lib/core/prelude'
import { routeForAnonymous } from '~/utils/lib/layout/redirection'
import { getPasswordRestoreRoute } from '~/stores/account'
import { getListRoute as getLeaveRequestRoute, getTokenApprovalRoute as getLeaveRequestTokenApprovalRoute } from '~/stores/transat/leave-request/index'
import type { RouteLocationNormalizedGeneric } from 'vue-router'

const ERRORS = {
  UNKNOWN_LAYOUT_ITEM: 'unknown_layout_item',
  REDIRECTION_HASHMAP_NOT_FOUND: 'redirection_hashmap_not_found',
}

/**
 * @desc
 *    Discriminant property
 *
 *    If a new redirection is needed, please __add a new entry__
 */
type LAYOUT_ITEM = typeof LAYOUT_ITEM[keyof typeof LAYOUT_ITEM]
const LAYOUT_ITEM = {
  LEAVE_REQUEST_LIST_PAGE: 'leave_request',
  LEAVE_REQUEST_TOKEN_APPROVAL: 'leave_request_token_approval',
  PASSWORD_RESTORE: 'password_restore',
} as const

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      middleware: [
        function(to) {
          const console = new Console('middleware:proxy')

          try {

            console.log('capture proxy redirection', JSON.stringify(to))

            const item = extractLayoutItem(to.query)
            const redirection = hasmap_item_has_redirection(item, to.query)

            // Making vanilla redirection to avoid any "vue-router" throws
            return navigateTo(redirection)

          } catch (err) {

            console.warn(
              toString(err),
              `making an automatic redirection`
            )

            // Making vanilla redirection to avoid any "vue-router" throws
            return navigateTo({ path: routeForAnonymous() })
          }
        }
      ]
    })
  },
  render() {
    return h('div')
  },
})
/**
 * @desc Extracting the layout navigation item where the redirection will be
 *  made
 * @throws {Error}
 */
function extractLayoutItem(queryParams :RouteLocationNormalizedGeneric["query"]) :LAYOUT_ITEM {
  const layoutItem = propOr('', 'layout_item', queryParams)
  const isValidLayoutItem = pipe_( values(LAYOUT_ITEM), includes(layoutItem) )

  if (not(isValidLayoutItem))
    throw new Error(ERRORS.UNKNOWN_LAYOUT_ITEM)

  return layoutItem as LAYOUT_ITEM
}
/**
 * @desc Hashmap build redirection URL according to provided `ITEM`
 * @throws {Error}
 */
function hasmap_item_has_redirection(item :LAYOUT_ITEM, queryParams :RouteLocationNormalizedGeneric["query"]) {
  switch (item) {

    case LAYOUT_ITEM.LEAVE_REQUEST_LIST_PAGE: {
      const dataId      = (queryParams.data_id ?? '') as string
      const companyCode = (queryParams.company_code ?? '') as string

      return getLeaveRequestRoute(dataId, companyCode)
    }

    case LAYOUT_ITEM.LEAVE_REQUEST_TOKEN_APPROVAL: {
      const uuidData    = (queryParams.uuid_data ?? '') as string
      const companyCode = (queryParams.company_code ?? '') as string
      const token       = (queryParams.token ?? '') as string

      return getLeaveRequestTokenApprovalRoute(uuidData, companyCode, token)
    }

    case LAYOUT_ITEM.PASSWORD_RESTORE: {
      const token       = (queryParams.token ?? '') as string

      return getPasswordRestoreRoute(token)
    }

    default:
      throw new Error(ERRORS.REDIRECTION_HASHMAP_NOT_FOUND)
  }
}
</script>
